import { Observable } from 'rxjs';

/** POST SSE stream. Fetch bypasses Angular interceptors, so attach the token explicitly. */
export function httpChatFrames(
  path: string, body: string, token: string | undefined, maxFrameBytes: number,
): Observable<string> {
  return new Observable(subscriber => {
    const controller = new AbortController();
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    const run = async () => {
      const url = new URL(path, window.location.origin);
      if (!path.startsWith('/api/') || !url.pathname.startsWith('/api/') || url.origin !== window.location.origin ||
          url.search || url.hash || url.username || url.password || !token) {
        throw new Error('Invalid authenticated endpoint');
      }
      const response = await fetch(url.href, {
        method: 'POST', body, signal: controller.signal, redirect: 'error', cache: 'no-store',
        headers: {
          'Content-Type': 'application/json', Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok || response.headers.get('content-type')?.split(';')[0].trim()
          .toLowerCase() !== 'text/event-stream' || !response.body) {
        throw new Error('Invalid streaming response');
      }
      reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8', { fatal: true });
      let line = '';
      let data: string[] = [];
      let size = 0;
      let afterCR = false;
      const consume = (text: string) => {
        for (const character of text) {
          if (subscriber.closed) return;
          if (afterCR && character === '\n') { afterCR = false; continue; }
          afterCR = character === '\r';
          // Iteration yields Unicode code points; count UTF-8 bytes without allocations.
          const codePoint = character.codePointAt(0)!;
          size += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
          if (size > maxFrameBytes) throw new Error('SSE frame too large');
          if (character !== '\r' && character !== '\n') { line += character; continue; }
          if (!line) {
            if (data.length) subscriber.next(data.join('\n'));
            data = []; size = 0;
          } else if (line === 'data' || line.startsWith('data:')) {
            data.push(line === 'data' ? '' : line.slice(5).replace(/^ /, ''));
          }
          line = '';
        }
      };
      try {
        while (!subscriber.closed) {
          const { value, done } = await reader.read();
          if (done) { consume(decoder.decode()); break; }
          consume(decoder.decode(value, { stream: true }));
        }
        if (!subscriber.closed) subscriber.complete();
      } finally {
        await reader.cancel().catch(() => undefined);
        reader.releaseLock();
      }
    };
    void run().catch(() => {
      if (!subscriber.closed) subscriber.error(new Error('Assistant HTTP transport failed'));
    });
    return () => {
      controller.abort();
      void reader?.cancel().catch(() => undefined);
    };
  });
}
